"use client";

/**
 * A command or URL a reader copies, with a Copy button: the public /mcp page's
 * setup commands.
 *
 * A client LEAF on a prerendered page (see `learn.prerender.test.ts`'s
 * `CLIENT_LEAVES_ON_PRERENDERED_PAGES`): the text is in the server HTML, so a
 * crawler and a reader without JavaScript still get the command; only the
 * button needs hydration. It must read no request state.
 *
 * Feedback is inline in a live region, never a toast: the public pages mount
 * no toast container, so a toast here would fail without a trace.
 */
import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

export interface LearnCodeBlockProps {
  code: string;
  /** What the code is, for the button's accessible name ("Copy <label>"). */
  label: string;
}

const COPY_FAILED_MESSAGE = "Couldn't copy. Select the text and copy it yourself.";

/**
 * How long "Copied" stays in the live region. A live region speaks only when
 * its text changes, so it must be empty again before the next click or a
 * repeat copy is silent. Shorter than the hook's 2 s button reset.
 */
const ANNOUNCE_MS = 1000;

export function LearnCodeBlock({ code, label }: LearnCodeBlockProps) {
  const { copied, copy } = useCopyToClipboard();
  const [announcement, setAnnouncement] = useState("");
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (clearTimer.current) clearTimeout(clearTimer.current);
    },
    [],
  );

  const handleCopy = async () => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
    setAnnouncement("");
    // The command stays on screen and selectable, so a blocked clipboard has
    // nothing to recover beyond telling the reader to copy by hand.
    const ok = await copy(code, { onError: () => setAnnouncement(COPY_FAILED_MESSAGE) });
    if (!ok) return;
    setAnnouncement("Copied");
    clearTimer.current = setTimeout(() => setAnnouncement(""), ANNOUNCE_MS);
  };

  const failed = announcement === COPY_FAILED_MESSAGE;

  return (
    <div className="my-3">
      <div className="flex items-start gap-2 rounded-xl border border-slate-300 bg-landing-bg-light py-2.5 pl-4 pr-2">
        <pre className="min-w-0 flex-1 overflow-x-auto py-1 text-[14px] leading-[1.55] text-landing-dark">
          <code className="font-mono">{code}</code>
        </pre>
        <button
          type="button"
          onClick={() => void handleCopy()}
          aria-label={`Copy ${label}`}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-landing-secondary transition-colors hover:border-lev-orange/50 hover:text-landing-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lev-orange"
        >
          {copied ? (
            <Check aria-hidden="true" className="h-3.5 w-3.5 text-lev-orange-dark" />
          ) : (
            <Copy aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          <span aria-hidden="true">{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      {/* Sighted readers see "Copied" on the button, so only a failure needs
          visible text; a screen reader hears both through the live region. */}
      <p role="status" className={failed ? "mt-1.5 text-[13px] text-landing-secondary" : "sr-only"}>
        {announcement}
      </p>
    </div>
  );
}
